import { ensureNumber, getAllActions } from "../comp/MissionUtils"

import { toZonedTime } from "date-fns-tz"
import { isAfter } from "date-fns"
import { eachDayOfInterval } from "date-fns/eachDayOfInterval"
import { getTimesheetHoursFromTo, getTimesheetHours } from "../comp/DateUtils"
import { isWithinInterval } from "date-fns"
import { getCurrencySymbol } from "../comp/Currencies"
import isWeekend from "date-fns/isWeekend"
import numeral from "numeral"
import { formatRateTimeUnits } from "../utils/dates"
import { withRateCardEntry } from "../utils/roleRates"

import { getRateOnOrBefore } from "../utils/exchange-rates"
import flatMap from "lodash/flatMap"
import { makeHash } from "../utils/utils"
import { getAllActionItems } from "../utils/actionItem"
import { isMissionBillable as isMissionBillableUtil } from "../utils/mf-utils"
import { getActionItemCostRevenue as getActionItemCostRevenueUtil } from "../utils/mf-utils"
import { getHourlyRates as getHourlyRatesUtil } from "../utils/mf-utils"
import { getLineItemCostAndRevenue as getLineItemCostAndRevenueUtil } from "../utils/mf-utils"
import { missionFinancials as missionFinancialsUtil } from "../utils/mf-utils"
import { getRoleItemCostAndRevenue as getRoleItemCostAndRevenueUtil } from "../utils/mf-utils"
import { getRaciBudgetForRole as getRaciBudgetForRoleUtil } from "../utils/mf-utils"
import { getHoursNeedFromRaci as getHoursNeedFromRaciUtil } from "../utils/mf-utils"
import { getFxForTheDay as getFxForTheDayUtil } from "../utils/mf-utils"
import { getBudgetWindow } from "./task-budget-dates"

function getObjectId(id, handlePopulatedRef = true) {
    if (!id) {
        return id
    }

    if (typeof id === "string") {
        return id
    }

    if (id._id && handlePopulatedRef === true) {
        // Populated reference. Re-iterate once with child _id
        return getObjectId(id._id, false)
    }

    return id
}

export function getHoursNeedFromRaci(r, ai, from, to) {
    return getHoursNeedFromRaciUtil(r, ai, from, to)
}

export function isMissionBillable(mission) {
    return isMissionBillableUtil(mission)
}

/**
 * Keeps the role budgets on tasks in step with their tasks: a budget's own window follows
 * its task (utils/task-budget-dates.js) and its hours are counted again over the task's
 * dates. One save for both, so neither overwrites the other.
 *
 * @param ai {object} Only this task's budgets - every task's otherwise
 * @param mission {object} The project, with the tasks' new dates
 * @param app {object}
 * @param [taskMoves] {object[]} The tasks whose dates just changed (getTaskMoves)
 */
/**
 * @param [taskMoves] {Array} Tasks whose dates moved: the budget windows on them follow.
 *   The API makes the same move in the task save itself (missionPlan.taskFollow.js), so
 *   plan items with a moved budget are only brought up to date here, never saved - saving
 *   them too could move them twice.
 */
export const updateBudgetOnRaciTasks = ({ ai, mission, app, taskMoves = [] }) => {
    const myPlanItems = mission.planItems.filter((p) => {
        return ai ? p.raciOnTasks?.filter((r) => r.taskId === ai._id) : p.raciOnTasks?.length
    })

    let changesNeeded = []
    let followedChanges = []

    const aiHash = makeHash(getAllActionItems(mission))

    myPlanItems.forEach((pi) => {
        let newRacis = []
        let raciChangesMade = false
        let followed = false

        pi.raciOnTasks?.forEach((r) => {
            const myAi = aiHash[r.taskId]
            const move = taskMoves.find((m) => m.taskId === r.taskId)
            const window = move ? getBudgetWindow(r, move.before, move.after) : null
            const budget = window ? { ...r, ...window } : r

            if (move) followed = true

            const myHoursNeeded = getHoursNeedFromRaci(budget, myAi)

            if (window || myHoursNeeded !== r.hoursNeeded) {
                newRacis.push({
                    ...budget,
                    hoursNeeded: myHoursNeeded,
                })
                raciChangesMade = true
            } else {
                newRacis.push(r)
            }
        })

        if (raciChangesMade) {
            let totalHoursNeeded = ensureNumber(newRacis?.reduce((cumm, r) => (cumm += ensureNumber(r.hoursNeeded)), 0))

            ;(followed ? followedChanges : changesNeeded).push({
                obj: pi,
                change: {
                    _id: pi._id,
                    raciOnTasks: newRacis,
                    hoursNeeded: totalHoursNeeded,
                },
            })
        }
    })

    if (followedChanges.length) {
        app.updateStateForPlanItemUpdateBulk(followedChanges)
    }

    if (changesNeeded.length) {
        app.planItemUpdateBulk(mission._id, changesNeeded)
    }
}

export const getDurationFromRaci = (mission, ai) => {
    const myPlanItems = mission.planItems.filter((p) => {
        return p.raciOnTasks?.filter((r) => r.taskId === ai._id)
    })
    let dur = 0
    myPlanItems.forEach((pi) => {
        pi.raciOnTasks?.forEach((r) => {
            dur += ensureNumber(getHoursNeedFromRaci(r, ai))
        })
    })

    return dur
}

export function missionFinancials(props) {
    return missionFinancialsUtil(props)
}

export function getActionPercentComplete(action) {
    const { actionItems } = action

    return (
        actionItems?.reduce((cumm, ai) => (cumm += ensureNumber(ai.percentComplete)), 0) ||
        ensureNumber(action.percentComplete)
    )
}

export function getRoleItemCostAndRevenue(props) {
    return getRoleItemCostAndRevenueUtil(props)
}

export function getLineItemCostAndRevenue(props) {
    return getLineItemCostAndRevenueUtil(props)
}

export function getHourlyRates(planItem, raciObj) {
    return getHourlyRatesUtil(planItem, raciObj)
}

export function getMilestoneRevenue({ planItem, from, to }) {
    if (from && to && isAfter(from, to)) {
        return 0
    }

    let isInRange = !from && !to

    if (!isInRange) {
        try {
            isInRange = isWithinInterval(toZonedTime(planItem.endDate), {
                start: from,
                end: to,
            })
        } catch (e) {}
    }

    if (isInRange) {
        return ensureNumber(planItem.billRate)
    }

    return 0
}

export function getActionItemCostRevenue(props) {
    return getActionItemCostRevenueUtil(props)
}

export function getMissionHours({ mission, timesheets, from, to }) {
    let hours = {
        planned: 0,
        actual: 0,
    }

    if (mission.projectType === "mx-gantt") {
        const rt = mission.planItems.flatMap((p) => p.raciOnTasks || []).filter(Boolean)

        const allAi = getAllActionItems(mission)

        allAi.forEach((ai) => {
            if (!rt.find((t) => t.taskId === ai._id)) {
                hours.planned +=
                    eachDayOfInterval({
                        start: from || toZonedTime(ai.startDate),
                        end: to || toZonedTime(ai.endDate),
                    })?.filter((d) => !isWeekend(d))?.length * 8 || 8
            }
        })

        mission.planItems
            .filter((p) => {
                return p.type === "person"
            })
            .forEach((pi) => {
                const d = getRaciBudgetForRole({
                    planItem: pi,
                    mission,
                    from,
                    to,
                })

                hours.planned += ensureNumber(d.hoursNeeded)
            })
    } else {
        mission.planItems
            .filter((p) => {
                return p.type === "person"
            })
            .forEach((planItem) => {
                const myData = getHoursForRole({ mission, planItem, from, to })
                hours.planned += ensureNumber(myData?.planned)
            })
    }

    timesheets?.forEach((ts) => {
        if (getObjectId(ts.mission) !== mission._id || ts.status !== "approved") {
            return
        }

        hours.actual += from && to ? getTimesheetHoursFromTo(ts, from, to) : getTimesheetHours(ts)
    })

    return hours
}

export function formatRates({ mission, orgData, pi, type }) {
    if (!type || !pi) {
        return ""
    }

    const rate = pi[type === "cost" ? "rate" : "billRate"]

    let unit = pi[type === "cost" ? "rateTime" : "billUnit"]

    if (!unit) {
        unit = orgData.roleDefaultRateTime
    }

    if (orgData?.currency && mission.currency !== orgData.currency) {
        const cur = type === "cost" ? orgData?.currency : mission.currency
        return getCurrencySymbol(cur) + numeral(rate).format("0,0.[00]") + cur + " " + formatRateTimeUnits(unit)
    } else {
        return getCurrencySymbol(mission.currency) + numeral(rate).format("0,0.[00]") + formatRateTimeUnits(unit)
    }
}

export function getHoursForRole({ mission, planItem, timesheets = [], from, to }) {
    let hours = {
        planned: 0,
        actual: 0,
    }

    if (mission.projectType === "mx-gantt") {
        //getRaciBudgetForRole already sums hoursNeeded across every raciOnTasks row -
        //calling it once per row multiplied planned hours by raciOnTasks.length
        const h = getRaciBudgetForRole({
            mission,
            planItem,
            timesheets: timesheets.filter((t) => t.planItemId === planItem._id),
            from,
            to,
        })

        hours.planned += ensureNumber(h.hoursNeeded)
    } else {
        hours.planned =
            planItem.hoursNeeded ||
            eachDayOfInterval({
                start: toZonedTime(planItem.startDate),
                end: toZonedTime(planItem.endDate),
            }).filter((d) => !isWeekend(d)).length * 8
    }

    timesheets?.forEach((ts) => {
        if (ts.planItemId === planItem._id && ts.status === "approved") {
            hours.actual += from && to ? getTimesheetHoursFromTo(ts, from, to) : getTimesheetHours(ts)
        }
    })

    return hours
}

export async function getExpenseTotal({ expenses, currency, statuses = ["approved", "paid"] }) {
    let total = 0

    for (const exp of expenses) {
        if (!statuses.includes(exp.status)) continue

        if (exp.currency !== currency) {
            // The rate in effect that day: the last one published on or before it
            const fx = await getRateOnOrBefore({ baseCurrency: currency, currency: exp.currency, day: exp.expenseDate })
            // What was paid, tip and taxes included: the same amount the budget's actuals count
            const myTotal = ensureNumber(getOneExpenseAmount(exp))

            total += myTotal / (fx || 1)
        } else {
            total += ensureNumber(getOneExpenseAmount(exp))
        }
    }

    return total
}

export function getExpenseAmount(exp, orgData) {
    let expenseAmount = ensureNumber(exp.amount)

    if (orgData?.expenseInvoiceOptions?.includes("tip")) {
        expenseAmount += ensureNumber(exp.tip)
    }
    if (orgData?.expenseInvoiceOptions?.includes("tax")) {
        expenseAmount += ensureNumber(exp.tax1) + ensureNumber(exp.tax2)
    }

    return expenseAmount
}
export function getAllChildrenThatAreActions(id, actionData) {
    let allChildren = []
    function getChildren(myId) {
        let immediateChildren = actionData.filter((d) => d.parentId === myId)
        allChildren = [...allChildren, ...immediateChildren]
        immediateChildren.forEach((item) => {
            getChildren(item._id)
        })
    }

    getChildren(id)

    return allChildren
}
export function getActionHours({ action, mission }) {
    if (action.estHours) {
        return ensureNumber(action.estHours)
    }
    const allActionKids = getAllChildrenThatAreActions(action._id, getAllActions(mission))

    const allTaskKids = [...(action.actionItems || []), ...flatMap(allActionKids || [], "actionItems")].filter(Boolean)

    let hours = 0

    if (allTaskKids.length === 0) {
        const d = eachDayOfInterval({
            start: toZonedTime(action.startDate),
            end: toZonedTime(action.endDate),
        }).filter((d) => !isWeekend(d)).length

        hours += d * 8
    } else {
        allTaskKids.forEach((ai) => {
            if (ai.actualDuration) {
                hours += ensureNumber(ai.actualDuration)
            } else {
                const d = eachDayOfInterval({
                    start: toZonedTime(ai.startDate),
                    end: toZonedTime(ai.endDate),
                }).filter((d) => !isWeekend(d)).length

                hours += ensureNumber(d * 8)
            }
        })
    }

    return ensureNumber(hours)
}

export const getRaciBudgetForRole = (planItem) => {
    return getRaciBudgetForRoleUtil(planItem)
}

export function getProcessTaskBudget({ ai, mission }) {
    return getActionItemCostRevenue({ ai, mission, totals: true })
}

export function getRoleRates({ role, orgData, mission }) {
    if (!role) {
        return role
    }

    if (mission.client) {
        const rc = orgData.rateCards?.find((rc) => rc.usedForId === mission.client)

        if (rc) {
            // Only the prices the card holds for the role; the rest are the role's own
            return withRateCardEntry(
                role,
                rc.ratesByRole?.find((r) => r.roleId === role._id),
            )
        }
    }

    return role
}

export function getFxForTheDay({ useMultiplier, exchangeRates, currency, day, app }) {
    return getFxForTheDayUtil({ useMultiplier, exchangeRates, currency, day, app })
}

export function getOneExpenseAmount(expense) {
    return (
        ensureNumber(expense.tip) +
        ensureNumber(expense.amount) +
        ensureNumber(expense.tax1) +
        ensureNumber(expense.tax2)
    )
}
